import { z } from "zod";
import dayjs from "dayjs";

// Base schedule schema for single schedule creation (editing)
export const scheduleSchema = z
  .object({
    user_id: z.number().min(1, "Please select a staff member"),
    table_id: z.number().optional().nullable(),
    schedule_date: z.string().min(1, "Please select a date"),
    start_time: z.string().min(1, "Please select start time"),
    end_time: z.string().min(1, "Please select end time"),
    shift_type: z.enum(["morning", "day", "night"], {
      required_error: "Please select a shift type",
    }),
    status: z.enum(["scheduled", "completed", "canceled"], {
      required_error: "Please select a status",
    }),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      // Validate that end time is after start time
      if (data.start_time && data.end_time) {
        const start = dayjs(`2000-01-01 ${data.start_time}`);
        const end = dayjs(`2000-01-01 ${data.end_time}`);
        return end.isAfter(start);
      }
      return true;
    },
    {
      message: "End time must be after start time",
      path: ["end_time"],
    }
  )
  .refine(
    (data) => {
      // Validate that schedule date is not in the past
      if (data.schedule_date) {
        const scheduleDate = dayjs(data.schedule_date);
        const today = dayjs().startOf("day");
        return scheduleDate.isAfter(today) || scheduleDate.isSame(today);
      }
      return true;
    },
    {
      message: "Schedule date cannot be in the past",
      path: ["schedule_date"],
    }
  );

// Bulk schedule schema for creating multiple schedules (new API)
export const bulkScheduleSchema = z
  .object({
    user_id: z
      .array(z.number())
      .min(1, "Please select at least one staff member"),
    table_id: z.array(z.number()).min(1, "Please select at least one table"),
    start_date: z.string().min(1, "Please select start date"),
    end_date: z.string().min(1, "Please select end date"),
    start_time: z.string().min(1, "Please select start time"),
    end_time: z.string().min(1, "Please select end time"),
    shift_type: z.enum(["morning", "day", "night"], {
      required_error: "Please select a shift type",
    }),
    status: z.enum(["scheduled", "completed", "canceled"], {
      required_error: "Please select a status",
    }),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      // Validate that end time is after start time
      if (data.start_time && data.end_time) {
        const start = dayjs(`2000-01-01 ${data.start_time}`);
        const end = dayjs(`2000-01-01 ${data.end_time}`);
        return end.isAfter(start);
      }
      return true;
    },
    {
      message: "End time must be after start time",
      path: ["end_time"],
    }
  )
  .refine(
    (data) => {
      // Validate that start date is not in the past
      if (data.start_date) {
        const startDate = dayjs(data.start_date);
        const today = dayjs().startOf("day");
        return startDate.isAfter(today) || startDate.isSame(today);
      }
      return true;
    },
    {
      message: "Start date cannot be in the past",
      path: ["start_date"],
    }
  )
  .refine(
    (data) => {
      // Validate that end date is not before start date
      if (data.start_date && data.end_date) {
        const startDate = dayjs(data.start_date);
        const endDate = dayjs(data.end_date);
        return endDate.isAfter(startDate) || endDate.isSame(startDate);
      }
      return true;
    },
    {
      message: "End date cannot be before start date",
      path: ["end_date"],
    }
  )
  .refine(
    (data) => {
      // Validate that date range doesn't exceed 30 days
      if (data.start_date && data.end_date) {
        const startDate = dayjs(data.start_date);
        const endDate = dayjs(data.end_date);
        const daysDiff = endDate.diff(startDate, "day") + 1;
        return daysDiff <= 30;
      }
      return true;
    },
    {
      message: "Date range cannot exceed 30 days",
      path: ["end_date"],
    }
  );

// Edit schedule schema (for updating existing schedules)
export const editScheduleSchema = z
  .object({
    user_id: z.number().min(1, "Please select a staff member").optional(),
    table_id: z.number().optional().nullable(),
    schedule_date: z.string().min(1, "Please select a date").optional(),
    start_time: z.string().min(1, "Please select start time").optional(),
    end_time: z.string().min(1, "Please select end time").optional(),
    shift_type: z
      .enum(["morning", "day", "night"], {
        required_error: "Please select a shift type",
      })
      .optional(),
    status: z
      .enum(["scheduled", "completed", "canceled"], {
        required_error: "Please select a status",
      })
      .optional(),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      // Validate that end time is after start time (if both are provided)
      if (data.start_time && data.end_time) {
        const start = dayjs(`2000-01-01 ${data.start_time}`);
        const end = dayjs(`2000-01-01 ${data.end_time}`);
        return end.isAfter(start);
      }
      return true;
    },
    {
      message: "End time must be after start time",
      path: ["end_time"],
    }
  );

// Type definitions
export type ScheduleFormData = z.infer<typeof scheduleSchema>;
export type BulkScheduleFormData = z.infer<typeof bulkScheduleSchema>;
export type EditScheduleFormData = z.infer<typeof editScheduleSchema>;

// Helper function to validate time range
export const validateTimeRange = (
  startTime: string,
  endTime: string
): boolean => {
  const start = dayjs(`2000-01-01 ${startTime}`);
  const end = dayjs(`2000-01-01 ${endTime}`);
  return end.isAfter(start);
};

// Helper function to validate date range
export const validateDateRange = (
  startDate: string,
  endDate: string
): {
  isValid: boolean;
  error?: string;
} => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const today = dayjs().startOf("day");

  if (start.isBefore(today)) {
    return { isValid: false, error: "Start date cannot be in the past" };
  }

  if (end.isBefore(start)) {
    return { isValid: false, error: "End date must be after start date" };
  }

  const daysDiff = end.diff(start, "day") + 1;
  if (daysDiff > 30) {
    return { isValid: false, error: "Date range cannot exceed 30 days" };
  }

  return { isValid: true };
};
